// Single source of truth for auth state in the app.
// Components read { user, ready, error, signIn, signOut } from useAuth()
// instead of importing the firebase auth object directly.
//
// Uses signInWithRedirect (not popup) — popups break on mobile + trip COOP
// warnings on modern desktop browsers. Redirect works everywhere.
import { useEffect, useState } from 'react';
import {
  signInWithRedirect,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  getRedirectResult,
  getIdTokenResult,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase.js';

const BOOTSTRAP_ADMIN_EMAILS = new Set([
  'bjanssens1113@gmail.com',
]);

function isBootstrapAdmin(user) {
  return BOOTSTRAP_ADMIN_EMAILS.has(String(user?.email || '').toLowerCase());
}

export function useAuth() {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If we just came back from a sign-in redirect, surface any error.
    // The user state itself is set by onAuthStateChanged below.
    getRedirectResult(auth).catch((e) => setError(e));

    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const token = await getIdTokenResult(u);
          setClaims(token.claims || {});
        } catch (e) {
          console.error('Could not read auth claims:', e);
          setClaims({});
        }
      } else {
        setClaims({});
      }
      setReady(true);
    });
  }, []);

  async function refreshClaims() {
    if (!auth.currentUser) {
      setClaims({});
      return {};
    }
    const token = await getIdTokenResult(auth.currentUser, true);
    setClaims(token.claims || {});
    return token.claims || {};
  }

  async function signIn() {
    setError(null);
    try {
      await signInWithRedirect(auth, googleProvider);
      // signInWithRedirect navigates the page away; nothing else runs here.
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  async function signInWithEmail(email, password) {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  async function signUpWithEmail(email, password) {
    setError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  // Send the reset email.
  //
  // Resolves the SAME WAY whether or not the address has an account.
  // Firebase throws auth/user-not-found, and passing that through
  // would turn this form into a way to test whether any given email
  // has a Bayless account — an enumeration oracle on a page anyone
  // can reach. The caller shows one confirmation either way.
  //
  // Real failures (network, rate limit, malformed address) still
  // throw, because those are worth telling someone about.
  async function resetPassword(email) {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, String(email || '').trim());
    } catch (e) {
      if (e?.code === 'auth/user-not-found' || e?.code === 'auth/invalid-email') return;
      setError(e);
      throw e;
    }
  }

  async function signOut() {
    setError(null);
    try {
      await fbSignOut(auth);
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  const isAdmin = !!claims.admin || !!claims.superAdmin || isBootstrapAdmin(user);
  const isSuperAdmin = !!claims.superAdmin || isBootstrapAdmin(user);

  return {
    user, claims, isAdmin, isSuperAdmin, ready, error,
    signIn, signInWithEmail, signUpWithEmail, signOut, refreshClaims,
    resetPassword,
  };
}
